import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import MFACodeInput from '../../../components/TextInputs/MFACodeInput'
import BigModal from '../../../components/Modals/BigModal'
import { UserContext } from '../../../App'
import { useNavigate } from 'react-router-dom'
import MFALogin from '../../../helpers/auth/MFALogin'
import PasskeySettings from './PasskeySettings'

const Config2FA = () => {
  const [loading, setLoading] = useState(true)
  const [qrCode, setQrCode] = useState('')
  const [manualCode, setManualCode] = useState('')
  const [alreadyConfigured, setAlreadyConfigured] = useState(false)
  const [newCodeRequested, setNewCodeRequested] = useState(false)
  const [codeInput, setCodeInput] = useState('')
  const [codeRejected, setCodeRejected] = useState(false)
  const [userPhoneMissing, setUserPhoneMissing] = useState(false)
  const { user } = useContext(UserContext)
  const navigate = useNavigate()
  // Passkey enrol/remove needs a fresh MFA step-up; when a passkey action
  // 403s we swap the page for MFALogin, then resume the action on success.
  const [mfaRetry, setMfaRetry] = useState(null)

  useEffect(() => {
    API.post('/auth/mfa/check-mfa-configured').then((res) => {
      if (res.status === 200) {
        if (res.data.isConfigured) {
          setAlreadyConfigured(true)
        } else {
          API.post('/auth/mfa/get-first-qr-code').then((res) => {
            if (res.status === 200) {
              setQrCode(res.data.qrCode)
              setManualCode(res.data.manualCode)
            } else if (res.status === 403) {
              setAlreadyConfigured(true)
            }
          })
        }
      }
    })
  }, [])

  const handleRequestNew = () => {
    const personnelInfo = user?.employee?.personnelInfo
    if (!personnelInfo?.phoneNumber1 && !personnelInfo?.phoneNumber2) {
      setUserPhoneMissing(true)
      return
    }
    API.post('/auth/mfa/request-new-code').then((res) => {
      if (res.status === 200) {
        setNewCodeRequested(true)
      }
      if (res.status === 406 && res.data === 'noUserPhoneNumber') {
        setUserPhoneMissing(true)
      }
    })
  }

  const handleSendCode = () => {
    setCodeRejected(false)
    API.post('/auth/mfa/request-new-code-2', { token: codeInput }).then(
      (res) => {
        if (res.status === 200) {
          console.log(res.data)
          setQrCode(res.data.qrCode)
          setManualCode(res.data.manualCode)
        } else {
          setCodeRejected(true)
          setCodeInput('')
        }
      },
    )
  }

  if (mfaRetry) {
    return (
      <MFALogin
        onSuccess={() => {
          const retry = mfaRetry
          setMfaRetry(null)
          retry()
        }}
      />
    )
  }

  return (
    <Stack gap={2}>
      <Stack gap={2} component={Paper} padding={1}>
        <Typography variant="title2">Two-Factor Authentication</Typography>
        <Alert severity="info">
          2FA is required for accessing enhanced features of AmbuNet, such as
          Patient Care Records.
        </Alert>
        {alreadyConfigured && (
          <Alert
            severity="success"
            action={
              <Button onClick={handleRequestNew} color="inherit" size="small">
                Request New
              </Button>
            }
          >
            You have already configured 2FA.
          </Alert>
        )}
        {qrCode && (
          <Stack padding={1} flexWrap="wrap">
            <Grid container spacing={1}>
              <Grid item xs={12} lg={4}>
                <Stack gap={1} alignItems="center">
                  <Box
                    component="img"
                    src={qrCode}
                    sx={{ width: '10rem', height: '10rem' }}
                  />
                  <Typography variant="caption">{manualCode}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} lg={8}>
                <Stack padding={1}>
                  <Typography variant="title3">Authenticator App</Typography>
                  <Typography variant="body2">
                    Authenticator apps and browser extensions like 1Password,
                    Authy, Microsoft Authenticator, etc. generate one-time
                    passwords that are used as a second factor to verify your
                    identity when prompted during sign-in.
                  </Typography>
                </Stack>
                <Stack gap={1} padding={1}>
                  <Typography variant="title3">Scan the QR Code</Typography>
                  <Typography variant="body2">
                    Use an authenticator app or browser extension to scan.
                    Unable to scan? You can use the Setup Key to manually
                    configure your authenticator app.
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        )}
        <Dialog
          open={userPhoneMissing}
          onClose={() => setUserPhoneMissing(false)}
        >
          <DialogTitle align="center" color="primary.main">
            No Phone Number
          </DialogTitle>
          <DialogContent>
            <Stack alignItems="center" gap={1}>
              <Typography variant="body2" textAlign="center">
                You cannot configure two-factor authentication without a valid
                mobile number on your record.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/my-account')}
              >
                Click here to change
              </Button>
            </Stack>
            {/* TODO: Add a new phone number here */}
          </DialogContent>
        </Dialog>
        {newCodeRequested && !qrCode && (
          <BigModal
            autoWidth
            open={newCodeRequested && !qrCode}
            onClose={() => setNewCodeRequested(false)}
          >
            <Stack padding={1} gap={1}>
              <Typography variant="title3">
                We've sent you a verification code to your mobile. Please enter
                it below.
              </Typography>
              <MFACodeInput
                value={codeInput}
                onChange={(newCode) => setCodeInput(newCode)}
              />
              <Button
                onClick={handleSendCode}
                size="small"
                variant="contained"
                disabled={codeInput.length < 6}
              >
                Submit
              </Button>
              {codeRejected && (
                <Alert severity="error">Code Invalid. Please try again.</Alert>
              )}
            </Stack>
          </BigModal>
        )}
      </Stack>
      {alreadyConfigured && (
        <PasskeySettings onRequireMFA={(retry) => setMfaRetry(() => retry)} />
      )}
    </Stack>
  )
}

export default Config2FA
